import React, { Component } from 'react';
import Markdown from 'markdown-to-jsx';

// Presentational chat surface — the ONE chat UI reused by the Chat Tester today and a future
// embeddable chat widget. It owns NO transport: the host passes `messages` + `onSend` and
// decides where the socket lives. Message shapes it renders:
//   { role:'user', text }
//   { role:'assistant', text, meta:{ botId, routedFrom, provider, model, usage, tools } }
//   { role:'event', event:'guardrail'|'transfer'|'hangup'|'flow', ...fields }
//
// Props: messages[], onSend(text), busy, showInspector, title, disabled, placeholder,
//        onReset, statusText.
class ChatPanel extends Component {
    constructor(props) {
        super(props);
        this.state = { input: '' };
        this.endRef = React.createRef();
    }

    componentDidUpdate() {
        if (this.endRef.current) this.endRef.current.scrollIntoView({ behavior: 'smooth' });
    }

    submit = () => {
        var text = (this.state.input || '').trim();
        if (!text || this.props.busy || this.props.disabled) return;
        this.setState({ input: '' });
        if (this.props.onSend) this.props.onSend(text);
    };

    onKeyDown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); this.submit(); } };

    renderInspector(meta) {
        if (!this.props.showInspector || !meta) return null;
        var u = meta.usage || {};
        var bits = [];
        if (meta.routedFrom) bits.push(meta.routedFrom + ' → ' + meta.botId);
        else if (meta.botId) bits.push('bot: ' + meta.botId);
        if (meta.model) bits.push((meta.provider ? meta.provider + '/' : '') + meta.model);
        if (u.input != null || u.output != null) bits.push('tok ' + (u.input || 0) + '↓/' + (u.output || 0) + '↑');
        if (meta.tools && meta.tools.length) bits.push('tools: ' + meta.tools.join(', '));
        if (typeof meta.failures === 'number' && meta.failures > 0) bits.push('failures: ' + meta.failures);
        if (!bits.length) return null;
        return <div style={styles.inspector}>{bits.join('  ·  ')}</div>;
    }

    renderEvent(m, i) {
        var label, color;
        if (m.event === 'guardrail') { label = '🛡️  Guardrail: ' + m.rule + ' (' + m.reason + ') → ' + m.action; color = '#8e44ad'; }
        else if (m.event === 'transfer') {
            var how = { phoneNumber: 'phone', genesysCloud: 'Genesys Cloud', amazonConnect: 'Amazon Connect' }[m.transferType] || m.transferType;
            var dest = m.number || m.acdQueueId || '';
            label = '↪︎  Transferred to ' + (m.queue || 'agent') + (how ? ' · via ' + how : '') + (dest ? ' ' + dest : '');
            color = '#2A6AA9';
        }
        else if (m.event === 'hangup') { label = '📴  Conversation ended' + (m.reason ? ' (' + m.reason + ')' : ''); color = '#c0392b'; }
        else if (m.event === 'flow') { label = '↩︎  Sent to IVR flow' + (m.flowId ? ' ' + m.flowId : ''); color = '#e67e22'; }
        else { label = m.event; color = '#888'; }
        return (
            <div key={i} style={styles.eventRow}>
                <span style={{ ...styles.eventPill, borderColor: color, color: color }}>{label}</span>
            </div>
        );
    }

    renderMessage(m, i) {
        if (m.role === 'event') return this.renderEvent(m, i);
        var isUser = m.role === 'user';
        var selectable = !isUser && !!this.props.onSelect;
        var selected = selectable && this.props.selectedIndex === i;
        return (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: isUser ? 'flex-end' : 'flex-start' }}>
                <div
                    onClick={selectable ? () => this.props.onSelect(i) : undefined}
                    title={selectable ? 'Inspect this turn' : undefined}
                    style={{ ...styles.bubble, ...(isUser ? styles.user : styles.assistant), ...(selectable ? { cursor: 'pointer' } : {}), ...(selected ? styles.selected : {}) }}
                >
                    {isUser
                        ? <span>{m.text}</span>
                        : <Markdown options={{ forceBlock: true }}>{m.text || ''}</Markdown>}
                </div>
                {!isUser && this.renderInspector(m.meta)}
            </div>
        );
    }

    render() {
        var messages = this.props.messages || [];
        var busy = this.props.busy;
        var disabled = this.props.disabled;
        return (
            <div style={styles.container}>
                <div style={styles.header}>
                    <span style={styles.title}>{this.props.title || 'Chat'}</span>
                    <span style={{ flex: 1 }} />
                    {this.props.statusText && <span style={styles.status}>{this.props.statusText}</span>}
                    {this.props.onReset && (
                        <button onClick={this.props.onReset} disabled={disabled} style={styles.resetBtn} title="Start over">Reset</button>
                    )}
                </div>

                <div style={styles.messages}>
                    {messages.map((m, i) => this.renderMessage(m, i))}
                    {busy && (
                        <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                            <div style={{ ...styles.bubble, ...styles.assistant, ...styles.breathing }}>…</div>
                        </div>
                    )}
                    <div ref={this.endRef} />
                </div>

                {!this.props.hideInput && (
                    <div style={styles.inputRow}>
                        <input
                            type="text"
                            value={this.state.input}
                            onChange={(e) => this.setState({ input: e.target.value })}
                            onKeyDown={this.onKeyDown}
                            placeholder={disabled ? (this.props.placeholder || 'Start a session to chat…') : (this.props.placeholder || 'Type a message…')}
                            disabled={busy || disabled}
                            style={styles.input}
                        />
                        <button onClick={this.submit} disabled={busy || disabled} style={(busy || disabled) ? styles.sendDisabled : styles.send}>Send</button>
                    </div>
                )}
            </div>
        );
    }
}

const styles = {
    container: { display: 'flex', flexDirection: 'column', height: '100%', width: '100%', border: '1px solid #dde3ea', borderRadius: '8px', overflow: 'hidden', background: '#fff' },
    header: { display: 'flex', alignItems: 'center', gap: '10px', background: '#002f5f', color: '#fff', padding: '12px 16px' },
    title: { fontSize: '15px', fontWeight: 700 },
    status: { fontSize: '12px', color: 'rgba(255,255,255,0.7)' },
    resetBtn: { background: 'transparent', border: '1px solid rgba(255,255,255,0.4)', color: '#fff', borderRadius: '5px', padding: '4px 10px', fontSize: '12px', cursor: 'pointer' },
    messages: { flex: 1, padding: '14px 16px', overflowY: 'auto', background: '#f4f6f9', display: 'flex', flexDirection: 'column', gap: '6px' },
    bubble: { padding: '9px 12px', borderRadius: '10px', maxWidth: '78%', fontSize: '14px', lineHeight: '1.5', wordBreak: 'break-word' },
    user: { background: '#2A6AA9', color: '#fff', borderBottomRightRadius: '3px' },
    assistant: { background: '#fff', color: '#1a1a1a', border: '1px solid #e6e9ef', borderBottomLeftRadius: '3px' },
    selected: { boxShadow: '0 0 0 2px #8e44ad', borderColor: '#8e44ad' },
    inspector: { fontSize: '11px', color: '#8a93a0', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', margin: '2px 4px 4px', maxWidth: '78%' },
    eventRow: { display: 'flex', justifyContent: 'center', margin: '4px 0' },
    eventPill: { fontSize: '12px', fontWeight: 600, border: '1px solid', borderRadius: '999px', padding: '3px 12px', background: '#fff' },
    inputRow: { display: 'flex', gap: '8px', padding: '10px 12px', borderTop: '1px solid #e6e9ef', background: '#fff' },
    input: { flex: 1, padding: '9px 12px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '14px', outline: 'none' },
    send: { padding: '9px 18px', background: '#002f5f', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' },
    sendDisabled: { padding: '9px 18px', background: '#c7cfd8', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '14px', fontWeight: 600, cursor: 'not-allowed' },
    breathing: { animation: 'chatpanel-breathing 1.4s ease-in-out infinite' },
};

// Inject the typing-bubble keyframes once (id-guarded so multiple imports don't duplicate it).
if (typeof document !== 'undefined' && !document.getElementById('chatpanel-keyframes')) {
    var sheet = document.createElement('style');
    sheet.id = 'chatpanel-keyframes';
    sheet.innerText = '@keyframes chatpanel-breathing { 0%{opacity:1} 50%{opacity:0.4} 100%{opacity:1} }';
    document.head.appendChild(sheet);
}

export default ChatPanel;
